/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, DoCheck, Inject, Input, OnChanges} from '@angular/core';
import template from './blocklist-edit-usage.component.html';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

interface UsageListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

interface UsageListView {
  items: any[];
  hasItems: boolean;
  pageSize: number;
  totalCount: number;
  currentPage: number;
}

@Component({
  standalone: false,
  selector: 'adn-blocklist-edit-usage',
  template
})
export class BlocklistEditUsageComponent implements OnChanges, DoCheck {
  @Input() isNew = false;
  @Input() publishers: any = {};
  @Input() sites: any = {};
  @Input() adUnits: any = {};

  activeTab: 'publishers' | 'sites' | 'adUnits' = 'publishers';

  /** Snapshots — parent list-helper replaces .items mid-CD (NG0100). */
  publisherView: UsageListView = emptyUsageListView();
  siteView: UsageListView = emptyUsageListView();
  adUnitView: UsageListView = emptyUsageListView();

  readonly publisherColumns: UsageListColumn[] = [
    {param: 'name'},
    {param: 'description'}
  ];

  readonly siteColumns: UsageListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'siteUrl'},
    {param: 'SiteGroup', label: 'siteGroup'},
    {param: 'EarningsAccount', label: 'earningsAccount'}
  ];

  readonly adUnitColumns: UsageListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'Site', label: 'site'}
  ];

  readonly perms: any;

  private lastPublishers: any;
  private lastPublisherItems: any;
  private lastSites: any;
  private lastSiteItems: any;
  private lastAdUnits: any;
  private lastAdUnitItems: any;
  private lastPublisherPage: any;
  private lastSitePage: any;
  private lastAdUnitPage: any;
  private syncScheduled = false;
  private initialized = false;

  constructor(
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.perms = LocalUserPermissions.getAllVisPermissions();
  }

  ngOnChanges(): void {
    this.scheduleListSync();
  }

  ngDoCheck(): void {
    this.scheduleListSync();
  }

  selectTab(tab: 'publishers' | 'sites' | 'adUnits'): void {
    this.activeTab = tab;
  }

  isColumnSortable(column: UsageListColumn): boolean {
    return column.sortable !== false && !!column.param;
  }

  columnLabel(column: UsageListColumn): string {
    if (column.labelInstant) {
      return column.labelInstant;
    }
    const key = column.label || column.param;
    return key ? 'label.' + key : '';
  }

  sortBy(list: any, column: UsageListColumn): void {
    if (!this.isColumnSortable(column) || !list?.makeSortQuery) {
      return;
    }
    if (column.numeric) {
      list.makeSortQuery(column.param, column.numeric);
    } else {
      list.makeSortQuery(column.param);
    }
  }

  changePage(list: any, page: number): void {
    if (!list || page < 1 || page > this.totalPages(list) || page === list.currentPage) {
      return;
    }
    list.currentPage = page;
    list.changePage();
  }

  totalPages(list: any): number {
    return Math.max(1, Math.ceil((list?.totalCount || 0) / (list?.pageSize || 1)));
  }

  pageNumbers(list: any): number[] {
    return visiblePageNumbers(list?.currentPage || 1, this.totalPages(list));
  }

  pageRangeStart(list: any): number {
    return list.pageSize * (list.currentPage - 1) + 1;
  }

  pageRangeEnd(list: any): number {
    return Math.min(list.pageSize * list.currentPage, list.totalCount);
  }

  private scheduleListSync(): void {
    if (!this.listsChanged()) {
      return;
    }
    if (!this.initialized) {
      this.initialized = true;
      this.applyListSnapshots();
      return;
    }
    if (this.syncScheduled) {
      return;
    }
    this.syncScheduled = true;
    deferMarkForCheck(this.cdr, () => {
      this.syncScheduled = false;
      this.applyListSnapshots();
    });
  }

  private listsChanged(): boolean {
    return this.publishers !== this.lastPublishers
      || this.publishers?.items !== this.lastPublisherItems
      || usagePageKey(this.publishers) !== this.lastPublisherPage
      || this.sites !== this.lastSites
      || this.sites?.items !== this.lastSiteItems
      || usagePageKey(this.sites) !== this.lastSitePage
      || this.adUnits !== this.lastAdUnits
      || this.adUnits?.items !== this.lastAdUnitItems
      || usagePageKey(this.adUnits) !== this.lastAdUnitPage;
  }

  private applyListSnapshots(): void {
    this.lastPublishers = this.publishers;
    this.lastPublisherItems = this.publishers?.items;
    this.lastPublisherPage = usagePageKey(this.publishers);
    this.lastSites = this.sites;
    this.lastSiteItems = this.sites?.items;
    this.lastSitePage = usagePageKey(this.sites);
    this.lastAdUnits = this.adUnits;
    this.lastAdUnitItems = this.adUnits?.items;
    this.lastAdUnitPage = usagePageKey(this.adUnits);
    this.publisherView = applyUsageListView(this.publishers);
    this.siteView = applyUsageListView(this.sites);
    this.adUnitView = applyUsageListView(this.adUnits);
  }
}

function emptyUsageListView(): UsageListView {
  return {items: [], hasItems: false, pageSize: 25, totalCount: 0, currentPage: 1};
}

function usagePageKey(list: any): string {
  return [list?.pageSize, list?.totalCount, list?.currentPage].join(':');
}

function applyUsageListView(list: any): UsageListView {
  const items = (list?.items || []).slice();
  return {
    items,
    hasItems: items.length > 0,
    pageSize: list?.pageSize || 25,
    totalCount: list?.totalCount || 0,
    currentPage: list?.currentPage || 1
  };
}
